import React from 'react';
import { motion } from 'framer-motion';
import { LucideIcon } from 'lucide-react';
import styled from 'styled-components';


const CardContainer = styled(motion.div) <{ $glow?: boolean }>`
  background: ${({ theme }) =>
    theme.isLight
      ? '#ffffff'
      : 'rgba(21, 21, 21, 0.7)'};
  backdrop-filter: ${({ theme }) => theme.isLight ? 'none' : 'blur(12px)'};
  -webkit-backdrop-filter: ${({ theme }) => theme.isLight ? 'none' : 'blur(12px)'};
  border: 1px solid ${({ theme }) => theme.isLight ? theme.colors.border : 'rgba(255, 255, 255, 0.05)'};
  border-radius: ${({ theme }) => theme.borders.radius.xl};
  padding: ${({ theme }) => theme.spacing[5]};
  position: relative;
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.isLight ? '0 4px 12px rgba(0, 0, 0, 0.05)' : '0 8px 32px 0 rgba(0, 0, 0, 0.3)'};
  transition: all 0.6s cubic-bezier(0.16, 1, 0.3, 1);
  cursor: pointer;
  min-height: 150px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  z-index: 1;

  ${({ $glow, theme }) => $glow && `
    box-shadow: ${theme.shadows.neon};
    border-color: ${theme.colors.error}44;
  `}

  &:hover {
    transform: translateY(-6px);
    box-shadow: ${({ theme }) =>
    theme.isLight
      ? theme.shadows.xl
      : `0 24px 48px -12px rgba(0, 0, 0, 0.6), 0 0 20px ${theme.colors.error}10`};
    border-color: ${({ theme }) => theme.colors.error}88;

    &::before {
      width: 100%;
      height: 4px;
      background: ${({ theme }) =>
    `linear-gradient(90deg, ${theme.colors.error} 0%, #ff6b6b 50%, ${theme.colors.error} 100%)`};
      box-shadow: 0 0 20px ${({ theme }) => theme.colors.error}66;
    }

    &::after {
      opacity: 1;
      transform: scale(1.1);
    }
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 40px;
    height: 3px;
    background: ${({ theme }) => theme.colors.error};
    opacity: 1;
    transition: all 0.6s cubic-bezier(0.16, 1, 0.3, 1);
    border-radius: 0 0 ${({ theme }) => theme.borders.radius.sm} 0;
    z-index: 5;
  }

  &::after {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: ${({ theme }) => `
      radial-gradient(circle at center, ${theme.colors.error}08 0%, transparent 40%)
    `};
    opacity: 0;
    transition: all 0.6s ease;
    z-index: 0;
    pointer-events: none;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  gap: ${({ theme }) => theme.spacing[2]};
`;

const CardTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.12em;
  margin: 0;
  line-height: 1.4;
  transition: all 0.3s ease;

  ${CardContainer}:hover & {
    color: ${({ theme }) => theme.colors.textPrimary};
    transform: translateX(2px);
  }
`;

const CardIcon = styled.div<{ $color: string }>`
  width: 38px;
  height: 38px;
  min-width: 38px;
  border-radius: 12px;
  background: ${({ theme }) => theme.isLight ? theme.colors.backgroundTertiary : 'rgba(255, 255, 255, 0.03)'};
  border: 1px solid ${({ theme }) => theme.isLight ? theme.colors.border : 'rgba(255, 255, 255, 0.08)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $color }) => $color};
  transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
  position: relative;
  z-index: 2;

  ${CardContainer}:hover & {
    background: ${({ $color }) => `${$color}15`};
    border-color: ${({ $color }) => `${$color}44`};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const CardValue = styled.div`
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  font-weight: 700;
  color: ${({ theme }) => theme.isLight ? theme.colors.textPrimary : theme.colors.textPrimary};
  margin-bottom: ${({ theme }) => theme.spacing[1]};
  line-height: 1;
  transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
  position: relative;
  z-index: 2;
  letter-spacing: -0.01em;

  ${CardContainer}:hover & {
    transform: scale(1.1) translateY(-2px);
    color: ${({ theme }) => theme.colors.error};
    text-shadow: ${({ theme }) => theme.isLight ? 'none' : `0 0 25px ${theme.colors.error}44`};
  }
`;

const CardChange = styled.div<{ $positive?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme, $positive }) => $positive ? theme.colors.success : theme.colors.error};
  margin-bottom: ${({ theme }) => theme.spacing[1]};
`;

const CardDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.isLight ? theme.colors.textTertiary : theme.colors.textTertiary};
  margin: 0;
  line-height: 1.5;
  transition: all 0.3s ease;
  position: relative;
  z-index: 2;
  font-weight: 400;

  ${CardContainer}:hover & {
    color: ${({ theme }) => theme.isLight ? theme.colors.textPrimary : theme.colors.textSecondary};
  }
`;

const TrendIndicator = styled.div<{ $positive?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoadingSkeleton = styled.div`
  background: ${({ theme }) =>
    theme.isLight
      ? `linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)`
      : `linear-gradient(90deg, #1A1A1A 25%, #2A2A2A 50%, #1A1A1A 75%)`};
  background-size: 200% 100%;
  animation: pulse 1.5s ease-in-out infinite;
  border-radius: ${({ theme }) => theme.borders.radius.sm};
  height: 20px;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  
  &:last-child {
    height: 48px;
    margin-bottom: 0;
  }
`;

interface StatsCardProps {
  title: string;
  value: string | number;
  change?: {
    value: string;
    positive: boolean;
  };
  icon: LucideIcon;
  iconColor: string;
  description?: string;
  glow?: boolean;
  loading?: boolean;
  $delay?: number;
}

const StatsCard: React.FC<StatsCardProps> = ({
  title,
  value,
  change,
  icon: Icon,
  iconColor,
  description,
  glow = false,
  loading = false,
  $delay = 0
}) => {
  if (loading) {
    return (
      <CardContainer $glow={glow}>
        <CardHeader>
          <div style={{ width: '60%' }}>
            <LoadingSkeleton style={{ height: '12px', width: '100%' }} />
          </div>
          <div style={{ width: '38px', height: '38px', borderRadius: '12px', background: 'rgba(255,255,255,0.05)' }} />
        </CardHeader>
        <LoadingSkeleton style={{ height: '32px', width: '80%' }} />
        <LoadingSkeleton style={{ height: '12px', width: '90%' }} />
      </CardContainer>
    );
  }

  return (
    <CardContainer
      $glow={glow}
      initial={{ opacity: 0, y: 20, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.5,
        delay: $delay * 0.5, // Faster entry
        ease: [0.23, 1, 0.32, 1]
      }}
      whileTap={{ scale: 0.98 }}
    >
      <div>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardIcon $color={iconColor}>
            <Icon size={18} />
          </CardIcon>
        </CardHeader>

        <CardValue>{value}</CardValue>
      </div>

      <div>
        {change && (
          <CardChange $positive={change.positive}>
            {change.positive ? '↑' : '↓'}
            <span>{change.value}</span>
          </CardChange>
        )}

        {description && (
          <CardDescription>{description}</CardDescription>
        )}
      </div>
    </CardContainer>
  );
};

export default StatsCard;

